import { createContext, PropsWithChildren, useMemo } from 'react';

import { useContextValue } from 'app/shared/hooks/use-context-value';
import { useSdkData } from 'app/shared/hooks/use-sdk-data/use-sdk-data.hook';

interface SdkCapabilitiesValue {
  /**
   * Host commands from the init handshake.
   * `null` until `initResponse` arrives; then a (possibly empty) list.
   */
  availableCmds: string[] | null;
}

const SdkCapabilitiesContext = createContext<SdkCapabilitiesValue>({ availableCmds: null });

/**
 * Captures the native host's `available_cmds` once, at the app root — where the
 * `initResponse` handler is registered before boot. The SDK does not replay
 * `initResponse`, so components that mount after boot (calendar / maps /
 * description links) must read capabilities from this context rather than
 * calling `useSdkData()` themselves and missing the event. See
 * `useOpenExternalLink`.
 */
export const SdkCapabilitiesProvider = ({ children }: PropsWithChildren): JSX.Element => {
  const { availableCmds } = useSdkData();
  const value = useMemo(() => ({ availableCmds }), [availableCmds]);

  return <SdkCapabilitiesContext.Provider value={value}>{children}</SdkCapabilitiesContext.Provider>;
};

export const useSdkCapabilities = (): SdkCapabilitiesValue => useContextValue(SdkCapabilitiesContext);

export { SdkCapabilitiesContext };
